/** Participant **assignment** APIs (**token** auth, **DD-SRS-F-TRAINING-QUIZ.28**). */

function bearerOnly(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

function bearerJson(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    "content-type": "application/json",
  };
}

export interface ParticipantTrainingDocument {
  training_document_id: string;
  ordinal: number;
  original_filename: string;
  extract_text: string;
}

export interface ParticipantQuestion {
  question_id: string;
  stem: string;
  choices: string[];
  /** **True** when the participant must select every correct option (checkbox UX). */
  multi_select?: boolean;
  /** Set on **`completed`** attempts: whether the selection had no false positives or negatives. */
  answered_correctly?: boolean;
}

export interface ParticipantAttemptSnapshot {
  attempt_id: string;
  status: string;
  ordered_questions: ParticipantQuestion[];
  /** Display indices (same order as **ordered_questions** choices) per question. */
  answers: Record<string, number[]>;
  score: number | null;
  passed: boolean | null;
  completed_at: string | null;
}

/**
 * Participant training document GET URL including invitation **token** (query). Clicking navigates through a **302**.
 *
 * @param quizId - Published quiz UUID.
 * @param trainingDocumentId - Document UUID from **`training_documents`** (**bootstrap** payload).
 * @param invitationToken - Raw invitation secret from the emailed link (**``?token=`**).
 */
export function participantTrainingDocumentDownloadUrl(
  quizId: string,
  trainingDocumentId: string,
  invitationToken: string,
): string {
  const path = `/api/v1/quizzes/${encodeURIComponent(quizId)}/participant/training-documents/${encodeURIComponent(trainingDocumentId)}`;
  const q = new URLSearchParams({
    token: invitationToken.trim(),
  });
  return `${path}?${q.toString()}`;
}

export interface ParticipantBootstrap {
  quiz_id: string;
  title: string;
  assignment_id: string;
  /** ISO **8601** timestamp when the organizer sent this invitation, if tracked. */
  assignment_issued_at?: string | null;
  participant_display_name?: string | null;
  configuration: {
    questions_per_attempt?: number;
    pass_percent?: number;
  };
  training_documents: ParticipantTrainingDocument[];
  /** Open non-terminal attempt (**none** once submitted until a retry is started). */
  in_progress_attempt: ParticipantAttemptSnapshot | null;
  /** Latest scored attempt when **`in_progress_attempt`** is **null** (**page refresh restores report**). */
  last_completed_attempt?: ParticipantAttemptSnapshot | null;
}

export async function fetchParticipantBootstrap(
  quizId: string,
  token: string,
  init?: RequestInit,
): Promise<Response> {
  const url = `/api/v1/quizzes/${encodeURIComponent(quizId)}/participant`;
  return fetch(url, {
    ...init,
    headers: { ...bearerOnly(token), ...init?.headers },
    credentials: "same-origin",
  });
}

/** Loads one attempt (**GET** **`/participant/attempts/:id`**) — same **`ParticipantAttemptSnapshot`** shape as **`in_progress_attempt`**. */
export async function fetchParticipantAttempt(
  quizId: string,
  attemptId: string,
  token: string,
  init?: RequestInit,
): Promise<Response> {
  return fetch(
    `/api/v1/quizzes/${encodeURIComponent(quizId)}/participant/attempts/${encodeURIComponent(attemptId)}`,
    {
      ...init,
      method: "GET",
      headers: { ...bearerOnly(token), ...init?.headers },
      credentials: "same-origin",
    },
  );
}

export async function postParticipantAttempt(
  quizId: string,
  token: string,
  init?: RequestInit & {
    /** Persisted on the assignment when non-empty after trim (**backend-api**). */
    participantDisplayName?: string;
  },
): Promise<Response> {
  const { participantDisplayName, ...rest } = init ?? {};
  const payload =
    participantDisplayName !== undefined
      ? { participant_display_name: participantDisplayName }
      : {};
  return fetch(
    `/api/v1/quizzes/${encodeURIComponent(quizId)}/participant/attempts`,
    {
      method: "POST",
      ...rest,
      headers: { ...bearerJson(token), ...rest.headers },
      body: JSON.stringify(payload),
      credentials: "same-origin",
    },
  );
}

export async function patchParticipantAttemptAnswer(
  quizId: string,
  attemptId: string,
  token: string,
  questionId: string,
  selectedIndex: number,
  init?: RequestInit,
): Promise<Response> {
  return patchParticipantAttemptSelection(
    quizId,
    attemptId,
    token,
    questionId,
    [selectedIndex],
    init,
  );
}

/** Persists one question's answer using display indices (single- or multi-select). */
export async function patchParticipantAttemptSelection(
  quizId: string,
  attemptId: string,
  token: string,
  questionId: string,
  selectedIndices: number[],
  init?: RequestInit,
): Promise<Response> {
  return fetch(
    `/api/v1/quizzes/${encodeURIComponent(quizId)}/participant/attempts/${encodeURIComponent(attemptId)}`,
    {
      method: "PATCH",
      ...init,
      headers: { ...bearerJson(token), ...init?.headers },
      body: JSON.stringify({
        question_id: questionId,
        selected_indices: selectedIndices,
      }),
      credentials: "same-origin",
    },
  );
}

export async function postParticipantSubmit(
  quizId: string,
  attemptId: string,
  token: string,
  init?: RequestInit,
): Promise<Response> {
  return fetch(
    `/api/v1/quizzes/${encodeURIComponent(quizId)}/participant/attempts/${encodeURIComponent(attemptId)}/submit`,
    {
      method: "POST",
      ...init,
      headers: { ...bearerJson(token), ...init?.headers },
      body: init?.body ?? "{}",
      credentials: "same-origin",
    },
  );
}

/** **DELETE** an in-progress **attempt**; body lists **last_completed_attempt** when one exists. */
export async function deleteParticipantAttempt(
  quizId: string,
  attemptId: string,
  token: string,
  init?: RequestInit,
): Promise<Response> {
  return fetch(
    `/api/v1/quizzes/${encodeURIComponent(quizId)}/participant/attempts/${encodeURIComponent(attemptId)}`,
    {
      method: "DELETE",
      ...init,
      headers: { ...bearerOnly(token), ...init?.headers },
      credentials: "same-origin",
    },
  );
}
